import { useMemo } from 'react';
import Hero from './components/Hero';
import TopPicksRightNow from './components/TopPicksRightNow';
import FeaturedGuide from './components/FeaturedGuide';
import CategorySection from './components/CategorySection';
import LatestBuyingGuides from './components/LatestBuyingGuides';
import WhyTrustUs from './components/WhyTrustUs';
import AboutSection from './components/AboutSection';
import JsonLd from '../../components/base/JsonLd';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://workplaysave.com';
const DEFAULT_OG_IMAGE = 'https://public.readdy.ai/ai/img_res/7965406aa664502cb6a3888418e2a788.jpg';

function SectionDivider({ tint }: { tint: 'purple' | 'cyan' | 'emerald' | 'amber' }) {
  const gradients: Record<string, string> = {
    purple:  'from-transparent via-purple-500/15 to-transparent',
    cyan:    'from-transparent via-cyan-500/15 to-transparent',
    emerald: 'from-transparent via-emerald-500/15 to-transparent',
    amber:   'from-transparent via-amber-500/15 to-transparent',
  };
  return (
    <div className={`w-full h-px bg-gradient-to-r ${gradients[tint]}`} />
  );
}

export default function HomePage() {
  const websiteSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Work Play Save',
    url: SITE_URL,
    description: 'Find the best gaming, streaming, and productivity tech with expert reviews and top-rated deals. Compare monitors, keyboards, and gear to upgrade your setup without overspending.',
    potentialAction: {
      '@type': 'SearchAction',
      target: `${SITE_URL}/search?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  }), []);

  const organizationSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Work Play Save',
    url: SITE_URL,
    description: 'Find the best gaming, streaming, and productivity tech with expert reviews and top-rated deals. Compare monitors, keyboards, and gear to upgrade your setup without overspending.',
    sameAs: [],
  }), []);

  return (
    <>
      <title>Work Play Save | Gaming, Streaming &amp; Tech Reviews</title>
      <meta name="description" content="Find the best gaming, streaming, and productivity tech with expert reviews and top-rated deals. Compare monitors, keyboards, and gear to upgrade your setup without overspending." />
      <meta name="keywords" content="best gaming monitors, best gaming keyboards, best gaming mice, streaming gear, webcams, microphones, office chairs, productivity monitors, tech reviews, gaming deals" />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={`${SITE_URL}/`} />
      <meta property="og:title" content="Work Play Save | Gaming, Streaming &amp; Tech Reviews" />
      <meta property="og:description" content="Find the best gaming, streaming, and productivity tech with expert reviews and top-rated deals. Compare monitors, keyboards, and gear to upgrade your setup without overspending." />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={`${SITE_URL}/`} />
      <meta property="og:image" content={DEFAULT_OG_IMAGE} />
      <meta property="og:site_name" content="Work Play Save" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Work Play Save | Gaming, Streaming &amp; Tech Reviews" />
      <meta name="twitter:description" content="Find the best gaming, streaming, and productivity tech with expert reviews and top-rated deals. Compare monitors, keyboards, and gear to upgrade your setup without overspending." />
      <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />
      <JsonLd data={websiteSchema} />
      <JsonLd data={organizationSchema} />
      <div className="min-h-screen bg-transparent">
        {/* 1. Hero */}
        <Hero />

        <SectionDivider tint="amber" />

        {/* 2. Top Picks Right Now */}
        <TopPicksRightNow />

        <SectionDivider tint="purple" />

        {/* 3. Featured Guide — rotates daily */}
        <FeaturedGuide />

        <SectionDivider tint="cyan" />

        {/* 4. Category Section */}
        <CategorySection />

        <SectionDivider tint="emerald" />

        {/* 5. Latest Buying Guides — auto-updates from articles.ts */}
        <LatestBuyingGuides />

        <SectionDivider tint="purple" />

        {/* 6. Why Trust Us */}
        <WhyTrustUs />

        <SectionDivider tint="amber" />

        {/* 7. About */}
        <AboutSection />

        <SectionDivider tint="cyan" />
      </div>
    </>
  );
}
